import React, { useCallback, useRef } from "react";

import { Id } from "react-toastify/unstyled";
import { Transaction } from "@common/types/transaction";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useFinalizeTransactionDeletion,
    useCancelTransactionDeletion,
    useMarkTransactionForDeletion,
} from "@api/transactionDeletion.api";
import { createTransactionDescription } from "./SoftDeletedTransactionToast.lib";
import { SoftDeletedTransactionToast } from "./SoftDeletedTransactionToast";

interface Props {
    transaction: Transaction;
    onBack: () => void;
    onDeleteSuccess: () => void;
    onHide?: () => void;
    show?: boolean;
}

export const DeleteTransactionModal: React.FC<Props> = ({
    transaction,
    onBack,
    onDeleteSuccess,
    onHide,
    show,
}) => {
    const toastId = useRef<Id | null>(null);
    const hasUndone = useRef(false);
    const { toast, dismiss } = useToaster();

    const onDelete = useMarkTransactionForDeletion();
    const onFinalize = useFinalizeTransactionDeletion();

    const cancelDeletion = useCancelTransactionDeletion();

    const finalizeTransaction = useCallback(async () => {
        await onFinalize.mutateAsync(transaction.id);
    }, [onFinalize, transaction.id]);

    const onToastHide = useCallback(async () => {
        if (!hasUndone.current) {
            await finalizeTransaction();
        }

        onHide?.();
    }, [finalizeTransaction, onHide]);

    const onUndo = useCallback(async () => {
        hasUndone.current = true;
        await cancelDeletion.mutateAsync(transaction.id);
        toast(
            `Restored transaction ${createTransactionDescription(transaction)}`,
        );
        if (toastId.current) {
            dismiss(toastId.current);
        }
    }, [cancelDeletion, transaction, toast, dismiss]);

    const deleteTransaction = async () => {
        hasUndone.current = false;
        await onDelete.mutateAsync(transaction.id);
        toastId.current = toast(
            <SoftDeletedTransactionToast
                transaction={transaction}
                undo={onUndo}
            />,
            transaction.id,
            { onClose: onToastHide },
        );
        onDeleteSuccess();
    };

    return (
        <ConfirmationModal
            title="Are you sure you want to delete this transaction?"
            question={`Transaction: ${createTransactionDescription(transaction)}`}
            no="Back"
            onReject={onBack}
            onConfirm={deleteTransaction}
            onHide={onHide}
            show={show}
        />
    );
};
